import React, { useEffect, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { FormCheck } from '../Form';
import Lucide from '../Lucide';

interface CheckboxSelectProps {
  options: string[];
  selected: string[];
  name: string;
  setValue: UseFormSetValue<any>;
  className?: string;
  count?: number;
  isObject?: boolean;
  objectValues?: any[];
  isString?: boolean;
  disabledOptions?: string[];
}

const CheckboxSelectV2: React.FC<CheckboxSelectProps> = ({
  options,
  selected,
  name,
  setValue,
  className,
  count,
  isString,
  disabledOptions = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    selected ?? []
  );

  const setValueRef = useRef(setValue);
  useEffect(() => {
    setValueRef.current = setValue;
  });

  const prevSelectedRef = useRef<string[]>(selected ?? []);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    const changed =
      prev.length !== selected?.length ||
      selected?.some((v, i) => v !== prev[i]);
    if (changed) {
      prevSelectedRef.current = selected ?? [];
      setSelectedOptions(selected ?? []);
    }
  }, [selected]);

  useEffect(() => {
    setValueRef.current(name, isString ? selectedOptions[0] : selectedOptions);
  }, [selectedOptions, name, isString]);

  const isDisabled = (option: string) =>
    disabledOptions.some((d) => d.toLowerCase() === option.toLowerCase());

  const updateSelected = (option: string) => {
    if (isDisabled(option)) return;
    setSelectedOptions((prev) => {
      if (prev.includes(option)) {
        return prev.filter((o) => o !== option);
      }
      if (count && prev.length >= count) {
        return [...prev.slice(1), option];
      }
      return [...prev, option];
    });
  };

  return (
    <div className={twMerge('relative', className)}>
      <div
        className='flex items-center justify-between bg-white border border-slate-300/60 rounded-md p-3 cursor-pointer text-sm'
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={selectedOptions.length > 0 ? '' : 'text-slate-500/70'}>
          {selectedOptions.length > 0
            ? selectedOptions.join(', ')
            : 'Select channel'}
        </span>
        <Lucide icon='ChevronDown' />
      </div>

      {isOpen && (
        <div className='absolute mt-1 w-full bg-white border border-slate-300/60 rounded-md shadow-lg z-10'>
          <div className='px-3 py-1'>
            {options.map((option) => {
              const disabled = isDisabled(option);
              return (
                <label
                  key={option}
                  className={twMerge(
                    'flex items-center gap-2 py-1 text-left text-sm',
                    disabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'cursor-pointer'
                  )}
                >
                  <FormCheck.Input
                    id={option}
                    type='checkbox'
                    value={option}
                    checked={selectedOptions.includes(option)}
                    onChange={() => updateSelected(option)}
                    disabled={disabled}
                    className='mr-2'
                  />
                  {option === 'Mail' ? 'Email' : option}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {isOpen && (
        <div className='fixed inset-0' onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};

export default CheckboxSelectV2;
